import { useMemo, useRef, useState, useEffect } from 'react'
import { useCopybookStore, getGridChars, getEffectiveLayout, FONT_GRID_RATIO } from '../store/copybookStore'
import { getZhuyin } from '../data/zhuyinUtils'
import { generatePDF } from './PdfExport'

// 定義不同紙張尺寸的寬高規格（單位：公釐 mm）
const PAPER_SIZES: Record<string, { width: number; height: number }> = {
  A4P: { width: 210, height: 297 }, // A4 直式
  A4L: { width: 297, height: 210 }, // A4 橫式
  A3P: { width: 297, height: 420 }, // A3 直式
  A3L: { width: 420, height: 297 }, // A3 橫式
  A5P: { width: 148, height: 210 }, // A5 直式
  A5L: { width: 210, height: 148 }, // A5 橫式
  B4P: { width: 250, height: 353 }, // B4 直式
  B4L: { width: 353, height: 250 }, // B4 橫式
  B5P: { width: 176, height: 250 }, // B5 直式
  B5L: { width: 250, height: 176 }, // B5 橫式
  // 向後相容舊版的 B5 設定
  B5: { width: 176, height: 250 },
}

export default function PreviewDOM() {
  const store = useCopybookStore()
  
  // 根據 store 中的設定取得當前紙張尺寸，若無對應則預設為 A4 直式
  const paper = PAPER_SIZES[store.paperSize] || PAPER_SIZES.A4P

  // 用於計算預覽縮放的容器 Ref 與縮放比例狀態
  const containerRef = useRef<HTMLDivElement>(null)
  const [autoScale, setAutoScale] = useState(1)
  const [zoomLevel, setZoomLevel] = useState(1)
  const [currentPageIndex, setCurrentPageIndex] = useState(0)
  const [viewMode, setViewMode] = useState<'single' | 'dual' | 'all'>('single')
  const [isGenerating, setIsGenerating] = useState(false)

  // 最終應用的縮放比例：自動適應比例 × 使用者自訂縮放
  const scale = autoScale * zoomLevel

  // 監聽容器大小變化，動態調整預覽頁面的縮放比例，以符合瀏覽器寬度
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const updateScale = () => {
      // 扣除左右內邊距以預留安全空間
      const availableWidth = container.clientWidth - (viewMode === 'dual' ? 70 : 48)
      // 將公釐（mm）轉換為像素（px），使用 1 mm = 3.7795275591 px 的比例
      const availableHeight = window.innerHeight - 240
      const paperWidthPx = paper.width * 3.7795275591 * (viewMode === 'dual' ? 2.08 : 1)
      const paperHeightPx = paper.height * 3.7795275591
      const widthScale = availableWidth > 0 ? availableWidth / paperWidthPx : 1
      const heightScale = availableHeight > 0 ? availableHeight / paperHeightPx : 1

      setAutoScale(Math.min(1, widthScale, heightScale))
    }

    updateScale()

    // 使用 ResizeObserver 監聽容器尺寸變化，以支援響應式視窗縮放
    const observer = new ResizeObserver(() => {
      updateScale()
    })
    observer.observe(container)

    return () => {
      observer.disconnect()
    }
  }, [paper.width, paper.height, viewMode])
  
  // 計算滿版或自訂的版面參數
  const layout = useMemo(() => {
    return getEffectiveLayout({
      paperSize: store.paperSize,
      marginTop: store.marginTop,
      marginBottom: store.marginBottom,
      marginLeft: store.marginLeft,
      marginRight: store.marginRight,
      gridSize: store.gridSize,
      colGap: store.colGap,
      rowGap: store.rowGap,
      colsPerRow: store.colsPerRow,
      printTitleAndSource: store.printTitleAndSource,
      title: store.title,
      subtitle: store.subtitle,
      autoFill: store.autoFill,
    })
  }, [
    store.paperSize,
    store.marginTop,
    store.marginBottom,
    store.marginLeft,
    store.marginRight,
    store.gridSize,
    store.colGap,
    store.rowGap,
    store.colsPerRow,
    store.printTitleAndSource,
    store.title,
    store.subtitle,
    store.autoFill,
  ])

  // 使用 useMemo 快取字元解析結果，避免不必要的重新計算
  const chars = useMemo(() => {
    const lineLength = store.fontDirection === 'vertical' ? layout.rows : layout.cols
    return getGridChars(store.text, {
      preserveLineBreaks: store.preserveLineBreaks,
      lineLength,
      copyInterval: store.copyInterval,
      copyBlankCount: store.copyBlankCount,
      copyRowInterval: store.copyRowInterval,
      copyRowBlankCount: store.copyRowBlankCount,
      showTrace: store.showTrace,
      traceInterval: store.traceInterval,
    })
  }, [
    store.text,
    store.showTrace,
    store.traceInterval,
    store.copyInterval,
    store.copyBlankCount,
    store.copyRowInterval,
    store.copyRowBlankCount,
    store.preserveLineBreaks,
    store.fontDirection,
    layout.rows,
    layout.cols,
  ])

  const cols = layout.cols
  const rowsPerPage = layout.rows
  const gridSize = layout.gridSize
  const colGap = layout.colGap
  const rowGap = layout.rowGap

  // 計算每頁總格子數（行數 * 列數）
  const cellsPerPage = useMemo(() => {
    return rowsPerPage * cols
  }, [rowsPerPage, cols])

  const getSourceIndexForCell = (cellIdx: number) => {
    if (store.fontDirection === 'horizontal') {
      return cellIdx
    }

    const row = Math.floor(cellIdx / cols)
    const col = cellIdx % cols
    const sourceCol = cols - 1 - col
    return sourceCol * rowsPerPage + row
  }

  const getCellChar = (pageChars: string[], cellIdx: number) => {
    return pageChars[getSourceIndexForCell(cellIdx)]
  }

  // 將所有字元根據每頁格子數分割成多個頁面
  // 與 PDF 匯出邏輯保持一致，且確保每頁都是滿格（以空字串填充）以維持 CSS Grid 的排列結構
  const pages = useMemo(() => {
    if (cellsPerPage <= 0) return []

    const pageList: string[][] = []
    let currentChars: string[] = []

    for (let i = 0; i < chars.length; i++) {
      currentChars.push(chars[i])
      if (currentChars.length === cellsPerPage) {
        pageList.push(currentChars)
        currentChars = []
      }
    }

    if (store.showCorrection) {
      if (currentChars.length > 0) {
        currentChars.push(...Array(Math.min(2, cellsPerPage - currentChars.length)).fill(''))
        currentChars.push(...Array(cellsPerPage - currentChars.length).fill(''))
        pageList.push(currentChars)
      } else if (pageList.length === 0) {
        currentChars.push('', '')
        currentChars.push(...Array(cellsPerPage - currentChars.length).fill(''))
        pageList.push(currentChars)
      }
    } else if (currentChars.length > 0) {
      currentChars.push(...Array(cellsPerPage - currentChars.length).fill(''))
      pageList.push(currentChars)
    }

    if (pageList.length === 0) {
      pageList.push(Array(cellsPerPage).fill(''))
    }

    return pageList
  }, [chars, cellsPerPage, store.showCorrection])

  // 當頁數縮減時，確保當前頁數不會超出範圍
  useEffect(() => {
    if (currentPageIndex >= pages.length) {
      setCurrentPageIndex(Math.max(0, pages.length - 1))
    }
  }, [pages.length, currentPageIndex])

  return (
    <div className="relative w-full h-[85vh] flex flex-col rounded-3xl border border-gray-200 bg-[#f8fafc] overflow-hidden shadow-inner print:block print:h-auto print:border-none print:bg-transparent print:shadow-none">
      {/* 預覽工具列 (Toolbar) - 支援響應式與行動端，防止浮動遮擋 */}
      <div className="flex flex-wrap items-center justify-between gap-2.5 bg-white border-b border-gray-200 px-4 py-2.5 z-10 print:hidden text-gray-700 shadow-sm">
        {/* 左側：檢視模式與頁面切換 */}
        <div className="flex flex-wrap items-center gap-2">
          {/* 視圖切換 */}
          <div className="flex bg-gray-100/90 rounded-xl p-0.5 border border-gray-200 text-xs font-semibold">
            <button
              type="button"
              onClick={() => setViewMode('single')}
              className={`px-3 py-1.5 rounded-lg transition-all ${viewMode === 'single' ? 'bg-white shadow text-blue-600 font-bold' : 'text-gray-500 hover:text-gray-800'}`}
              title="單頁居中預覽"
            >
              單頁
            </button>
            {pages.length > 1 && (
              <button
                type="button"
                onClick={() => setViewMode('dual')}
                className={`px-3 py-1.5 rounded-lg transition-all hidden sm:block ${viewMode === 'dual' ? 'bg-white shadow text-blue-600 font-bold' : 'text-gray-500 hover:text-gray-800'}`}
                title="雙頁對開排版"
              >
                雙頁對開
              </button>
            )}
            <button
              type="button"
              onClick={() => setViewMode('all')}
              className={`px-3 py-1.5 rounded-lg transition-all ${viewMode === 'all' ? 'bg-white shadow text-blue-600 font-bold' : 'text-gray-500 hover:text-gray-800'}`}
              title="向下連續滾動顯示所有頁面"
            >
              全部 ({pages.length})
            </button>
          </div>

          {/* 頁面切換按鈕（單頁/雙頁模式） */}
          {viewMode !== 'all' && pages.length > 1 && (
            <div className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-xl px-2 py-1">
              <button
                type="button"
                onClick={() => setCurrentPageIndex(idx => Math.max(0, viewMode === 'dual' ? idx - 2 : idx - 1))}
                disabled={currentPageIndex === 0}
                className="p-1 hover:bg-gray-200 hover:text-blue-600 rounded-lg transition-all disabled:opacity-30 disabled:hover:bg-transparent text-gray-500"
                title="上一頁"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <span className="text-xs font-bold font-mono px-1 min-w-[3rem] text-center text-gray-700">
                {viewMode === 'dual' 
                  ? `${currentPageIndex + 1}-${Math.min(pages.length, currentPageIndex + 2)} / ${pages.length}`
                  : `${currentPageIndex + 1} / ${pages.length}`}
              </span>

              <button
                type="button"
                onClick={() => setCurrentPageIndex(idx => Math.min(pages.length - 1, viewMode === 'dual' ? idx + 2 : idx + 1))}
                disabled={viewMode === 'dual' ? currentPageIndex >= pages.length - 2 : currentPageIndex === pages.length - 1}
                className="p-1 hover:bg-gray-200 hover:text-blue-600 rounded-lg transition-all disabled:opacity-30 disabled:hover:bg-transparent text-gray-500"
                title="下一頁"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          )}

          {/* 統計晶片 (Badges) */}
          <div className="hidden xl:flex items-center gap-1.5 text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-xl px-2.5 py-1">
            <span>字數 <strong className="text-blue-700 font-mono">{chars.filter(c => c.trim()).length}</strong></span>
            <span>·</span>
            <span>每頁 <strong className="text-gray-700 font-mono">{cellsPerPage}</strong> 格</span>
          </div>
        </div>

        {/* 右側：縮放控制與快捷列印/PDF按鈕 */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-xl px-2 py-0.5">
            <button 
              onClick={() => setZoomLevel(z => Math.max(0.25, Number((z - 0.15).toFixed(2))))}
              className="p-1 hover:bg-gray-200 hover:text-blue-600 rounded-lg transition-all active:scale-95 text-gray-600"
              title="縮小"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 12H4" /></svg>
            </button>
            <span className="text-xs font-bold min-w-[2.6rem] text-center font-mono text-gray-700">
              {Math.round(zoomLevel * 100)}%
            </span>
            <button 
              onClick={() => setZoomLevel(z => Math.min(3, Number((z + 0.15).toFixed(2))))}
              className="p-1 hover:bg-gray-200 hover:text-blue-600 rounded-lg transition-all active:scale-95 text-gray-600"
              title="放大"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" /></svg>
            </button>
            <div className="w-px h-3.5 bg-gray-300 mx-0.5"></div>
            <button 
              onClick={() => setZoomLevel(1)}
              className="px-2 py-0.5 text-[11px] font-semibold text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
              title="適應頁面寬度"
            >
              適應
            </button>
          </div>

          {/* 快捷列印與匯出 PDF */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => window.print()}
              className="hidden sm:flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-xl shadow-sm transition-all active:scale-95"
              title="直接使用瀏覽器列印"
            >
              <svg className="w-3.5 h-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              列印
            </button>

            <button
              disabled={isGenerating}
              onClick={async () => {
                setIsGenerating(true)
                try {
                  await generatePDF()
                } catch (err) {
                  console.error(err)
                  alert('產生 PDF 失敗，請檢查網路連線後重試')
                } finally {
                  setIsGenerating(false)
                }
              }}
              className={`flex items-center gap-1 px-3.5 py-1.5 text-xs font-bold text-white rounded-xl shadow-sm transition-all active:scale-95 ${
                isGenerating ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
              }`}
              title="匯出高解析向量字帖 PDF"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              {isGenerating ? '產生中...' : '匯出 PDF'}
            </button>
          </div>
        </div>
      </div>

      {/* 畫布工作區：具備現代化點狀背景 */}
      <div 
        ref={containerRef}
        className={`flex-1 overflow-auto p-4 sm:p-8 w-full print-container print:gap-0 print:p-0 print:block print:overflow-visible ${
          viewMode === 'dual' 
            ? 'flex flex-row flex-wrap justify-center items-start gap-8' 
            : 'flex flex-col gap-10 items-center'
        }`}
        style={{
          backgroundColor: '#f1f3f6',
          backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px), radial-gradient(#cbd5e1 1px, #f1f3f6 1px)',
          backgroundSize: '20px 20px',
          backgroundPosition: '0 0, 10px 10px',
        }}
      >
        {/* 遍歷每一頁，繪製頁面外框與網格內容 */}
        {pages.map((pageChars, pageIdx) => {
          const isHidden = 
            (viewMode === 'single' && pageIdx !== currentPageIndex) ||
            (viewMode === 'dual' && pageIdx !== currentPageIndex && pageIdx !== currentPageIndex + 1)
          return (
            <div
              key={pageIdx}
              className={`print-wrapper flex flex-col items-center gap-3 select-none print:block print:my-0 ${isHidden ? 'hidden print:block' : ''}`}
            >
              {/* 頁面頂部中繼資料徽章，增添專業排版工具質感 */}
              <div className="flex items-center justify-between w-full text-xs text-gray-500 font-medium bg-white/90 backdrop-blur-sm border border-gray-200/60 rounded-xl px-4 py-2 shadow-sm print:hidden">
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
                  第 {pageIdx + 1} 頁 / 共 {pages.length} 頁
                </span>
                <span className="font-mono text-[10px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                  {store.paperSize === 'A4P' ? 'A4 直式' : store.paperSize === 'A4L' ? 'A4 橫式' : store.paperSize} · {paper.width} × {paper.height} mm
                </span>
              </div>

              <div
                className="print-wrapper flex justify-center items-start"
                style={{
                  // 依據縮放比例動態調整外圍 wrapper 的寬高
                  width: `${paper.width * 3.7795275591 * scale}px`,
                  height: `${paper.height * 3.7795275591 * scale}px`,
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <div
                  className="bg-white shadow-[0_15px_30px_-5px_rgba(0,0,0,0.1),0_10px_15px_-5px_rgba(0,0,0,0.05)] hover:shadow-[0_25px_40px_-5px_rgba(0,0,0,0.15),0_15px_20px_-5px_rgba(0,0,0,0.08)] relative box-border flex flex-col print-sheet transition-shadow duration-300"
                  style={{
                    // 內層實際紙張的 mm 寬高
                    width: `${paper.width}mm`,
                    height: `${paper.height}mm`,
                    // 動態套用使用者設定的上下左右邊距
                    padding: `${store.marginTop}mm ${store.marginRight}mm ${store.marginBottom}mm ${store.marginLeft}mm`,
                    // 使用 CSS Transform 進行等比例縮放以適應螢幕
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    // 若沒有縮放（scale === 1）使用 relative 佈局，否則用 absolute 避免縮放偏移
                    position: scale === 1 ? 'relative' : 'absolute',
                    top: 0,
                    left: 0,
                  }}
                >
                  {/* 頁首標題與作者/出處區塊（固定高度 20mm） */}
                  {store.printTitleAndSource && (store.title || store.subtitle) && (
                    <div 
                      className="w-full flex flex-col items-center justify-center select-none"
                      style={{ height: '20mm' }}
                    >
                      {store.title && (
                        <h1 className="text-center font-bold text-gray-900 leading-none" style={{ fontSize: '6mm' }}>
                          {store.title}
                        </h1>
                      )}
                      {store.subtitle && (
                        <h2 className="text-center text-gray-500 mt-[1.5mm] leading-none" style={{ fontSize: '3.5mm' }}>
                          {store.subtitle}
                        </h2>
                      )}
                    </div>
                  )}

                  {/* 網格區域：自動填滿剩餘的垂直空間 */}
                  <div className="flex-1 flex items-start justify-center overflow-hidden">
                    <div
                      className="grid"
                      style={{
                        // 動態設定 Grid 的欄數與格子寬度
                        gridTemplateColumns: `repeat(${cols}, ${gridSize}mm)`,
                        gridTemplateRows: `repeat(${rowsPerPage}, ${gridSize}mm)`,
                        // 動態設定格子間的行距與列距
                        gap: `${rowGap}mm ${colGap}mm`,
                        // 計算 Grid 的總寬度，公式：(格子數 * 格子大小) + (間距數 * 間距)
                        width: `${cols * gridSize + (cols - 1) * colGap}mm`,
                        height: `${rowsPerPage * gridSize + (rowsPerPage - 1) * rowGap}mm`
                      }}
                    >
                      {/* 遍歷當前頁面的每一個字元格 */}
                      {pageChars.map((_, i) => {
                        // 計算該格子在整份字帖中的絕對索引位置
                        const sourceIndex = getSourceIndexForCell(i)
                        const absoluteIndex = pageIdx * cellsPerPage + sourceIndex
                        // 判斷是否需要顯示描紅（Trace）樣式：
                        // 若開啟描紅，且未設定間隔（或間隔為奇數格），則套用描紅樣式
                        const char = getCellChar(pageChars, i)
                        const trace = store.showTrace
                        const isHollow = store.showTrace && (
                          store.traceInterval 
                            ? (absoluteIndex % 2 === 1) 
                            : (store.traceType === 'hollow')
                        )
                        
                        const row = Math.floor(i / cols)
                        const col = i % cols

                        return (
                          <div
                            key={i}
                            className="relative box-border"
                          >
                            {/* 內層包裝：用以支援圓角與剪裁，避免剪裁到外部的注音/拼音 */}
                            <div
                              className="absolute inset-0 overflow-hidden"
                              style={{
                                borderRadius: `${store.gridRadius}px`,
                              }}
                            >
                              {/* SVG 向量背景輔助線（如田字格、米字格等） */}
                              <svg 
                                className="absolute inset-0 w-full h-full" 
                                viewBox="0 0 100 100"
                                preserveAspectRatio="none"
                                style={{ opacity: store.gridOpacity / 100 }}
                              >
                                {/* 為了避免相鄰格子邊框重疊導致粗細與透明度不均，分別繪製四邊 */}
                                <line x1="0" y1="0" x2="100" y2="0" stroke={store.gridColor || '#9ca3af'} strokeWidth={store.gridLineWidth} vectorEffect="non-scaling-stroke" />
                                <line x1="0" y1="0" x2="0" y2="100" stroke={store.gridColor || '#9ca3af'} strokeWidth={store.gridLineWidth} vectorEffect="non-scaling-stroke" />
                                {(colGap > 0 || col === cols - 1) && (
                                  <line x1="100" y1="0" x2="100" y2="100" stroke={store.gridColor || '#9ca3af'} strokeWidth={store.gridLineWidth} vectorEffect="non-scaling-stroke" />
                                )}
                                {(rowGap > 0 || row === rowsPerPage - 1) && (
                                  <line x1="0" y1="100" x2="100" y2="100" stroke={store.gridColor || '#9ca3af'} strokeWidth={store.gridLineWidth} vectorEffect="non-scaling-stroke" />
                                )}
                                {/* 田字格：繪製十字虛線 */}
                                {store.gridType === 'tian' && (
                                  <>
                                    <line x1="50" y1="0" x2="50" y2="100" stroke={store.gridColor || '#9ca3af'} strokeWidth={store.gridLineWidth} vectorEffect="non-scaling-stroke" strokeDasharray="2,2" />
                                    <line x1="0" y1="50" x2="100" y2="50" stroke={store.gridColor || '#9ca3af'} strokeWidth={store.gridLineWidth} vectorEffect="non-scaling-stroke" strokeDasharray="2,2" />
                                  </>
                                )}
                                {/* 米字格：繪製十字虛線及對角虛線 */}
                                {store.gridType === 'mi' && (
                                  <>
                                    <line x1="50" y1="0" x2="50" y2="100" stroke={store.gridColor || '#9ca3af'} strokeWidth={store.gridLineWidth} vectorEffect="non-scaling-stroke" strokeDasharray="2,2" />
                                    <line x1="0" y1="50" x2="100" y2="50" stroke={store.gridColor || '#9ca3af'} strokeWidth={store.gridLineWidth} vectorEffect="non-scaling-stroke" strokeDasharray="2,2" />
                                    <line x1="0" y1="0" x2="100" y2="100" stroke={store.gridColor || '#9ca3af'} strokeWidth={store.gridLineWidth} vectorEffect="non-scaling-stroke" strokeDasharray="2,2" />
                                    <line x1="100" y1="0" x2="0" y2="100" stroke={store.gridColor || '#9ca3af'} strokeWidth={store.gridLineWidth} vectorEffect="non-scaling-stroke" strokeDasharray="2,2" />
                                  </>
                                )}
                                {/* 回宮格：繪製內層正方形及十字虛線 */}
                                {store.gridType === 'huigong' && (
                                  <>
                                    <rect x="20" y="20" width="60" height="60" fill="none" stroke={store.gridColor || '#9ca3af'} strokeWidth={store.gridLineWidth} vectorEffect="non-scaling-stroke" strokeDasharray="2,2" />
                                    <line x1="50" y1="0" x2="50" y2="100" stroke={store.gridColor || '#9ca3af'} strokeWidth={store.gridLineWidth} vectorEffect="non-scaling-stroke" strokeDasharray="2,2" />
                                    <line x1="0" y1="50" x2="100" y2="50" stroke={store.gridColor || '#9ca3af'} strokeWidth={store.gridLineWidth} vectorEffect="non-scaling-stroke" strokeDasharray="2,2" />
                                  </>
                                )}
                                {/* 九宮格：繪製兩橫兩豎虛線以均分九大區域 */}
                                {store.gridType === 'jiugong' && (
                                  <>
                                    <line x1="33.3" y1="0" x2="33.3" y2="100" stroke={store.gridColor || '#9ca3af'} strokeWidth={store.gridLineWidth} vectorEffect="non-scaling-stroke" strokeDasharray="2,2" />
                                    <line x1="66.6" y1="0" x2="66.6" y2="100" stroke={store.gridColor || '#9ca3af'} strokeWidth={store.gridLineWidth} vectorEffect="non-scaling-stroke" strokeDasharray="2,2" />
                                    <line x1="0" y1="33.3" x2="100" y2="33.3" stroke={store.gridColor || '#9ca3af'} strokeWidth={store.gridLineWidth} vectorEffect="non-scaling-stroke" strokeDasharray="2,2" />
                                    <line x1="0" y1="66.6" x2="100" y2="66.6" stroke={store.gridColor || '#9ca3af'} strokeWidth={store.gridLineWidth} vectorEffect="non-scaling-stroke" strokeDasharray="2,2" />
                                  </>
                                )}
                              </svg>

                              {/* 文字呈現區塊 */}
                              {char && (
                                <div
                                  className="absolute inset-0 flex items-center justify-center font-zitie"
                                  style={{
                                    fontSize: `${gridSize * FONT_GRID_RATIO}mm`,
                                    fontFamily: store.fontFamily,
                                    writingMode: store.fontDirection === 'vertical' ? 'vertical-rl' : 'horizontal-tb',
                                    textOrientation: 'upright',
                                    // 描紅模式：直接使用描紅顏色，空心時使用透明填充搭配 text-stroke
                                    color: trace 
                                      ? (isHollow ? 'transparent' : store.traceColor) 
                                      : store.exampleColor,
                                    WebkitTextStroke: isHollow
                                      ? `${store.traceLineWidth}px ${store.traceColor}`
                                      : undefined,
                                  }}
                                >
                                  {char}
                                </div>
                              )}
                            </div>

                            {/* 注音標註（顯示在格子右側，縱向排列） */}
                            {store.showPhonetic && char && (() => {
                              const zy = getZhuyin(char)
                              if (!zy) return null
                              return (
                                <div
                                  className="absolute flex flex-col items-center justify-center select-none"
                                  style={{
                                    fontSize: `${gridSize * 0.18}mm`,
                                    color: '#888',
                                    right: `-${gridSize * 0.28}mm`,
                                    top: 0,
                                    bottom: 0,
                                    width: `${gridSize * 0.25}mm`,
                                    lineHeight: 1.1,
                                    letterSpacing: '0.5px',
                                    writingMode: 'vertical-rl',
                                    textOrientation: 'upright',
                                  }}
                                >
                                  {store.phoneticBlank ? '' : zy}
                                </div>
                              )
                            })()}
                          </div>
                        )
                      })}
                    </div>
                  </div>

                  {/* 頁尾與頁碼顯示區塊 */}
                  <div 
                    className="absolute left-0 right-0 flex items-center justify-center text-gray-400 select-none"
                    style={{ 
                      bottom: `${store.marginBottom / 2}mm`, 
                      height: '6mm', 
                      fontSize: '3.5mm',
                      paddingLeft: `${store.marginLeft}mm`,
                      paddingRight: `${store.marginRight}mm`
                    }}
                  >
                    {/* 自訂頁尾文字 */}
                    {store.footer && (
                      <div className="text-center w-full">
                        {store.footer}
                      </div>
                    )}
                    {/* 頁碼（顯示於右側） */}
                    {store.showPageNumber && (
                      <div className="absolute font-medium" style={{ right: `${store.marginRight}mm` }}>
                        {pageIdx + 1}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

